import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  FlaskConical, Code, Copy, Check, Play, Loader2, AlertTriangle,
  ChevronDown, ChevronRight, X, Eye, EyeOff, Hash, Clock, Globe,
  Camera, FileText, Wand2, Settings2, ChevronUp, ExternalLink,
  ArrowRight, Square, Download, Shield, KeyRound, Zap,
  Code2, Globe2, Sparkles, Server, Lock,
} from 'lucide-react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge, Dot } from '../components/Badge';
import { SectionHeader } from '../components/Tabs';
import { apiFetch } from '../api';
import { formatNumber, copyToClipboard } from '../lib/utils';
import { cn } from '../lib/utils';

// =============================================================================
// Client-side cost estimator. Mirrors the backend's PER_CALL_COSTS so users
// see the cost they're about to spend before clicking "Test".
// =============================================================================
const PER_CALL_COSTS = {
  classic_base: 1,
  stealth_base: 4,
  js_render_extra: 1,
  screenshot_extra: 2,
  pdf_extra: 1,
};

function estimateCost({ proxy_mode, js_render, screenshot, response_type }) {
  let base, baseLabel;
  if (proxy_mode === 'stealth') {
    base = PER_CALL_COSTS.stealth_base;
    baseLabel = 'stealth';
  } else {
    // null (dynamic) defaults to classic. The server will charge stealth
    // (4) if it actually needs Datames — we show the conservative estimate here.
    base = PER_CALL_COSTS.classic_base;
    baseLabel = proxy_mode || 'dynamic';
  }
  const extras = [];
  if (js_render) extras.push(['js_render', PER_CALL_COSTS.js_render_extra]);
  if (screenshot) extras.push(['screenshot', PER_CALL_COSTS.screenshot_extra]);
  if (response_type === 'pdf') extras.push(['pdf', PER_CALL_COSTS.pdf_extra]);
  const extrasTotal = extras.reduce((s, [, v]) => s + v, 0);
  return { base, baseLabel, extras, extrasTotal, total: base + extrasTotal };
}

// =============================================================================
// Default request — sensible starting point for the playground.
// =============================================================================
const DEFAULT_REQUEST = {
  // popcat.click — a popular test/easter-egg site that's reliably reachable
  // (returns 200, never 5xx-rate-limited like httpbin does). Real HTML so
  // users see meaningful content in the playground demo.
  url: 'https://popcat.click',
  method: 'GET',
  payload: null,
  js_render: false,
  js_instructions: '',
  custom_headers: null,
  proxy_mode: '', // '' = dynamic (no field sent)
  proxy_country: '',
  // Per-request proxy override. Empty strings mean "use the operator's
  // proxy slot above". To bypass the slot entirely for one call, set
  // proxy_url to "direct". To bring your own proxy, paste the URL here
  // (with or without embedded credentials) and optionally fill in
  // separate username/password fields.
  proxy_url: '',
  proxy_username: '',
  proxy_password: '',
  session_id: '',
  tag: '',
  timeout_ms: 30000,
  response_type: 'html',
  outputs: null,
  allowed_status_codes: [200, 201, 202, 203, 204, 206],
  wait_for: '',
  wait: 0,
  block_resources: true,
  json_response: false,
  css_extractor: null,
  autoparse: false,
  permissive_status: false,
  screenshot: false,
  screenshot_fullpage: false,
  screenshot_selector: '',
  screenshot_format: 'png',
  screenshot_quality: 90,
  viewport_width: 1920,
  viewport_height: 1080,
  device: 'desktop',
  cookies: null,
};

const LANGS = [
  { key: 'curl',       label: 'cURL' },
  { key: 'python',     label: 'Python (requests)' },
  { key: 'python_aio', label: 'Python (aiohttp)' },
  { key: 'node',       label: 'Node.js (fetch)' },
  { key: 'node_axios', label: 'Node.js (axios)' },
  { key: 'ruby',       label: 'Ruby' },
  { key: 'go',         label: 'Go' },
  { key: 'php',        label: 'PHP' },
  { key: 'java',       label: 'Java' },
  { key: 'csharp',     label: 'C#' },
  { key: 'rust',       label: 'Rust' },
  { key: 'shell',      label: 'Shell' },
];

// =============================================================================
// PlaygroundPage — two-column layout: builder on the left, code+result on the right.
// =============================================================================
export default function PlaygroundPage() {
  // --- Request state ---
  // Pre-fill from the user's API defaults (Settings → API defaults), so the
  // values they configured carry over to the Playground automatically.
  const initialRequest = useMemo(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('sa.api_defaults') || 'null');
      if (stored && typeof stored === 'object') {
        return {
          ...DEFAULT_REQUEST,
          proxy_mode:        stored.proxy_mode ?? DEFAULT_REQUEST.proxy_mode,
          proxy_country:     stored.country ?? DEFAULT_REQUEST.proxy_country,
          response_type:     stored.response_type ?? DEFAULT_REQUEST.response_type,
          screenshot_format: stored.screenshot_format ?? DEFAULT_REQUEST.screenshot_format,
          screenshot_quality:stored.screenshot_quality ?? DEFAULT_REQUEST.screenshot_quality,
          js_render:         stored.render_js ?? DEFAULT_REQUEST.js_render,
          block_resources:   stored.block_resources ?? DEFAULT_REQUEST.block_resources,
          permissive_status: stored.permissive_status ?? DEFAULT_REQUEST.permissive_status,
        };
      }
    } catch { /* ignore */ }
    return DEFAULT_REQUEST;
  }, []);
  const hasApiDefaults = useMemo(() => {
    try { return !!JSON.parse(localStorage.getItem('sa.api_defaults') || 'null'); }
    catch { return false; }
  }, []);
  const [req, setReq] = useState(initialRequest);
  const update = (patch) => setReq((r) => ({ ...r, ...patch }));

  // --- API key (auto-fetched on mount by rotating once) ---
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [keyNote, setKeyNote] = useState('');
  const [keyLoading, setKeyLoading] = useState(true);

  // Auto-fetch the active key on mount. Phase-9: uses the new
  // /v1/keys/active-plaintext endpoint which returns the current
  // active key's plaintext WITHOUT rotating. No more destroying keys
  // every time the playground opens.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const jwt = sess?.session?.access_token;
        if (!jwt) {
          if (!cancelled) {
            setKeyNote('Sign in to auto-fetch your API key.');
            setKeyLoading(false);
          }
          return;
        }
        // apiFetch returns parsed JSON; 404 throws an Error with .status === 404
        // (see api.js — it sets status + detail on thrown errors).
        let data;
        try {
          data = await apiFetch('/v1/keys/active-plaintext', {
            headers: { 'x-user-token': jwt },
          });
        } catch (e) {
          if (e?.status === 404) {
            if (!cancelled) {
              setKeyNote('No active API key. Create one from the API Keys page.');
              setKeyLoading(false);
            }
            return;
          }
          throw e;
        }
        if (!cancelled) {
          setApiKey(data.plaintext_key || '');
          setKeyNote(`Using your active key (created ${new Date(data.created_at).toLocaleDateString()}). No rotation performed.`);
          setKeyLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setKeyNote(`Could not auto-fetch key: ${e.message}. You can paste it below.`);
          setKeyLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // --- Response state ---
  // --- Response state ---
  const [result, setResult] = useState(null); // raw fetch response
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(null);

  // --- UI state ---
  // Default to Python (requests) — most users start with this.
  const [lang, setLang] = useState('python');
  const [copied, setCopied] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  // Result sub-tab: 'result' (raw body), 'browser' (HTML iframe), 'screenshot'
  const [resultTab, setResultTab] = useState('result');

  // --- Cost estimate (memoized) ---
  const cost = useMemo(
    () => estimateCost({ proxy_mode: req.proxy_mode, js_render: req.js_render, screenshot: req.screenshot, response_type: req.response_type }),
    [req.proxy_mode, req.js_render, req.screenshot, req.response_type],
  );

  // --- Code snippet (memoized) ---
  const snippet = useMemo(() => buildSnippet(req, lang, apiKey), [req, lang, apiKey]);

  // --- Reset result when request changes ---
  useEffect(() => { setResult(null); setError(null); }, [req.url, req.method, req.proxy_mode, req.proxy_url, req.response_type, req.screenshot]);

  // ===========================================================================
  // Handlers
  // ===========================================================================
  async function runTest() {
    if (!req.url.trim()) { setError('URL is required'); return; }
    setBusy(true);
    setError(null);
    setResult(null);
    setElapsedMs(null);
    const t0 = performance.now();
    try {
      const body = cleanPayload(req);
      const headers = {};
      if (apiKey) headers['x-api-key'] = apiKey;
      // apiFetch returns parsed JSON directly (not a Response object).
      // If it throws, the error carries status + detail — we catch below.
      const parsed = await apiFetch('/v1/fetch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
      });
      setResult(parsed);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setElapsedMs(Math.round(performance.now() - t0));
      setBusy(false);
    }
  }

  async function copySnippet() {
    const ok = await copyToClipboard(snippet);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  function resetRequest() {
    setReq(DEFAULT_REQUEST);
    setResult(null);
    setError(null);
  }

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Playground"
        description="Build a request, preview the code, and test it live. No docs needed."
      />

      {/* API key card — auto-fetched, with optional manual override */}
      <Card>
        <Card.Body className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 shrink-0">
              <KeyRound className="h-4 w-4 text-brand" />
              <span className="text-xs font-medium text-fg">API key</span>
              {keyLoading ? (
                <span className="inline-flex items-center gap-1 text-2xs text-fg-muted">
                  <Loader2 className="h-3 w-3 animate-spin" /> loading…
                </span>
              ) : apiKey ? (
                <Badge variant="success">
                  <Dot variant="success" /> Active
                </Badge>
              ) : (
                <Badge variant="destructive">
                  <Dot variant="destructive" /> No key
                </Badge>
              )}
            </div>
            <div className="relative flex-1">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={keyLoading ? 'loading…' : 'sk_...'}
                spellCheck={false}
                autoComplete="off"
                className="w-full h-9 pl-3 pr-10 text-sm font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/40"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-7 w-7 rounded-md text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors"
                title={showKey ? 'Hide' : 'Show'}
              >
                {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </div>
            <span className="text-2xs text-fg-subtle shrink-0">
              <a href="/keys" className="text-brand hover:underline">API Keys</a>
            </span>
          </div>
          {keyNote && (
            <div className={cn(
              'mt-2.5 px-2.5 py-1.5 rounded-md text-2xs flex items-start gap-1.5',
              apiKey
                ? 'bg-success-soft border border-success/20 text-success-fg'
                : 'bg-warning-soft border border-warning/20 text-warning-fg',
            )}>
              <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
              <span>{keyNote}</span>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Mobile-only stack / desktop two-col grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* ============================ LEFT: BUILDER ============================ */}
          <Card className="lg:col-span-2">
            <Card.Header>
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex items-center gap-2">
                  <Wand2 className="h-4 w-4 text-brand" />
                  <Card.Title>Request</Card.Title>
                  {hasApiDefaults && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-brand-softest border border-brand-soft text-2xs text-brand-strong">
                      <Sparkles className="h-2.5 w-2.5" />
                      Using your defaults
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={resetRequest}
                  className="text-2xs text-fg-muted hover:text-fg transition-colors"
                >
                  Reset
                </button>
              </div>
            </Card.Header>
            <Card.Body className="space-y-5">
              {/* --- Target --- */}
              <Section label="Target" icon={Globe}>
                <Field label="URL">
                  <input
                    type="url"
                    value={req.url}
                    onChange={(e) => update({ url: e.target.value })}
                    placeholder="https://example.com"
                    className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/40"
                  />
                </Field>
<div className="grid grid-cols-2 gap-3">
    <Field label="Method">
      <select
        value={req.method}
        onChange={(e) => update({ method: e.target.value })}
        className="w-full h-9 px-2 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
      >
        {['GET','POST','PUT','DELETE','HEAD','PATCH','OPTIONS'].map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
    </Field>
    <Field label="Response type">
      <select
        value={req.response_type}
        onChange={(e) => update({ response_type: e.target.value })}
        className="w-full h-9 px-2 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
      >
        <option value="html">HTML</option>
        <option value="text">Text</option>
        <option value="markdown">Markdown</option>
        <option value="pdf">PDF</option>
        {/* F-01: removed 'Screenshot' option. The backend only
            accepts html|text|markdown|pdf for response_type.
            Screenshots are controlled by the separate
            'Take a screenshot' toggle (which sets the
            `screenshot` boolean, NOT response_type). */}
      </select>
    </Field>
  </div>
</Section>

{/* --- Proxy mode (the big one) --- */}
<Section label="Proxy mode" icon={Shield}>
  <div className="grid grid-cols-3 gap-2">
    <ProxyOption
      active={req.proxy_mode === ''}
      onClick={() => update({ proxy_mode: '' })}
      label="Dynamic"
      sub="Auto-pick"
      cost="1"
      costLabel="best case"
    />
    <ProxyOption
      active={req.proxy_mode === 'classic'}
      onClick={() => update({ proxy_mode: 'classic' })}
      label="Classic"
      sub="Residential"
      cost="1"
      costLabel="credit"
    />
    <ProxyOption
      active={req.proxy_mode === 'stealth'}
      onClick={() => update({ proxy_mode: 'stealth' })}
      label="Stealth"
      sub="Datames"
      cost="4"
      costLabel="credits"
    />
  </div>
  <Field label="Proxy country (optional)" hint="ISO-2 code, e.g. US, DE, FR">
    <input
      type="text"
      value={req.proxy_country}
      maxLength={2}
      onChange={(e) => update({ proxy_country: e.target.value.toUpperCase() })}
      placeholder="US"
      className="w-full h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand uppercase"
    />
  </Field>
</Section>

{/* --- Custom proxy (per-request override) ---
    When all three fields are empty, the operator's proxy slot above is
    used. To bring your own proxy for a single call, paste the URL here.
    The URL can be `http://user:pass@host:port` (embedded credentials),
    or you can split credentials into the two fields below. Set
    proxy_url to "direct" to skip the operator slot entirely for this
    call (useful when the operator proxy is dead). */}
<Section label="Custom proxy (optional)" icon={Server}>
  <div className="flex items-start gap-2 px-2.5 py-2 rounded-md bg-surface-2 border border-border text-2xs text-fg-muted">
    <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5 text-brand" />
    <span>
      Leave empty to use the operator's <span className="font-mono">proxy_mode</span> slot
      above. Anything here overrides it for this single call.
    </span>
  </div>
  <Field
    label="Proxy URL"
    hint="http://user:pass@host:port — or 'direct' to skip the operator slot"
  >
    <input
      type="text"
      value={req.proxy_url}
      onChange={(e) => update({ proxy_url: e.target.value })}
      placeholder="http://user:pass@host:8080"
      className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand"
    />
  </Field>
  {req.proxy_url && req.proxy_url.toLowerCase() !== 'direct' && (
    <div className="grid grid-cols-2 gap-3 pl-1 border-l-2 border-brand/20 ml-1">
      <Field label="Username (optional)" hint="Overrides any creds in the URL">
        <input
          type="text"
          value={req.proxy_username}
          onChange={(e) => update({ proxy_username: e.target.value })}
          placeholder="user"
          className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand"
        />
      </Field>
      <Field label="Password (optional)" hint="Only used if username is set">
        <input
          type="password"
          value={req.proxy_password}
          onChange={(e) => update({ proxy_password: e.target.value })}
          placeholder="••••••"
          className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand"
        />
      </Field>
    </div>
  )}
</Section>

{/* --- Screenshot --- */}
<Section label="Screenshot" icon={Camera}>
  <Toggle
    label="Take a screenshot"
    description="Returns a base64 PNG/JPEG image. +2 credits."
    checked={req.screenshot}
    onChange={(v) => update({ screenshot: v })}
  />
  {/* F-02: removed the response_type='screenshot' toggle that
      used to break the request with a 422. The screenshot
      boolean alone is what the backend reads. */}
  {req.screenshot && (
    <div className="grid grid-cols-2 gap-3 pl-1 border-l-2 border-brand/20 ml-1">
      <Toggle
        label="Full page"
        description="Capture the whole scroll height"
        checked={req.screenshot_fullpage}
        onChange={(v) => update({ screenshot_fullpage: v })}
      />
      <Field label="Format">
        <select
          value={req.screenshot_format}
          onChange={(e) => update({ screenshot_format: e.target.value })}
          className="w-full h-9 px-2 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
        >
          <option value="png">PNG</option>
          <option value="jpeg">JPEG</option>
        </select>
      </Field>
      <Field label="CSS selector (optional)" hint="Capture just this element">
        <input
          type="text"
          value={req.screenshot_selector}
          onChange={(e) => update({ screenshot_selector: e.target.value })}
          placeholder=".main-content"
          className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand"
        />
      </Field>
      {req.screenshot_format === 'jpeg' && (
        <Field label="Quality">
          <input
            type="number"
            min="1"
            max="100"
            value={req.screenshot_quality}
            onChange={(e) => update({ screenshot_quality: parseInt(e.target.value) || 90 })}
            className="w-full h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
          />
        </Field>
      )}
    </div>
  )}
</Section>

{/* --- Engine --- */}
<Section label="Engine" icon={Settings2}>
  <Toggle
    label="Render JavaScript"
    description="Use a real browser. Required for SPAs. +1 credit."
    checked={req.js_render}
    onChange={(v) => update({ js_render: v })}
  />
  {req.js_render && (
    <Field label="JS instructions" hint="Custom script to run after load">
      <textarea
        value={req.js_instructions}
        onChange={(e) => update({ js_instructions: e.target.value })}
        placeholder="document.querySelectorAll('.cookie-banner').forEach(el => el.remove());"
        rows={3}
        className="w-full px-3 py-2 text-xs font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand resize-y"
      />
    </Field>
  )}
  <Toggle
    label="Block resources"
    description="Skip images, fonts, and trackers for faster loads"
    checked={req.block_resources}
    onChange={(v) => update({ block_resources: v })}
  />
</Section>

{/* --- Response --- */}
<Section label="Response" icon={FileText}>
  <Field label="Wait for" hint="CSS selector — pause until it appears">
    <input
      type="text"
      value={req.wait_for}
      onChange={(e) => update({ wait_for: e.target.value })}
      placeholder=".main-content"
      className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand"
    />
  </Field>
  <Field label="Extra wait (ms)" hint="After load">
    <input
      type="number"
      min="0"
      max="60000"
      value={req.wait}
      onChange={(e) => update({ wait: parseInt(e.target.value) || 0 })}
      className="w-full h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
    />
  </Field>
  <Toggle
    label="Permissive status"
    description="Return the body even if 4xx/5xx (caller sees success=false)"
    checked={req.permissive_status}
    onChange={(v) => update({ permissive_status: v })}
  />
  <Toggle
    label="Auto JSON parse"
    description="Return a parsed object instead of HTML"
    checked={req.json_response}
    onChange={(v) => update({ json_response: v })}
  />
  <Toggle
    label="Auto extract (heuristic)"
    description="Try to find the main article / product info"
    checked={req.autoparse}
    onChange={(v) => update({ autoparse: v })}
  />
  {/* F-06: outputs field — pick which fields the backend should
      return. Defaults to "all" (None). Picking specific fields
      shrinks the response payload and saves bandwidth. */}
  <Field label="Return fields" hint="Pick which fields the response includes (default = all)">
    <OutputsPicker
      value={req.outputs}
      onChange={(v) => update({ outputs: v })}
    />
  </Field>
</Section>

{/* --- Advanced (collapsed by default) --- */}
<button
  type="button"
  onClick={() => setAdvancedOpen((v) => !v)}
  className="flex items-center gap-1.5 text-2xs font-medium text-fg-muted hover:text-fg transition-colors w-full"
>
  {advancedOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
  Advanced
</button>
{advancedOpen && (
  <Section label="Advanced" icon={Settings2}>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Session ID" hint="Reuse a cf_clearance slot">
        <input
          type="text"
          value={req.session_id}
          onChange={(e) => update({ session_id: e.target.value })}
          placeholder="auto-generated if blank"
          className="w-full h-9 px-3 text-xs font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand"
        />
      </Field>
      <Field label="Tag" hint="For billing reports">
        <input
          type="text"
          value={req.tag}
          onChange={(e) => update({ tag: e.target.value })}
          placeholder="my-app"
          className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand"
        />
      </Field>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <Field label="Viewport W">
        <input type="number" min="320" max="3840" value={req.viewport_width}
          onChange={(e) => update({ viewport_width: parseInt(e.target.value) || 1920 })}
          className="w-full h-9 px-2 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand" />
      </Field>
      <Field label="Viewport H">
        <input type="number" min="240" max="2160" value={req.viewport_height}
          onChange={(e) => update({ viewport_height: parseInt(e.target.value) || 1080 })}
          className="w-full h-9 px-2 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand" />
      </Field>
      <Field label="Device">
        <select value={req.device} onChange={(e) => update({ device: e.target.value })}
          className="w-full h-9 px-2 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand">
          <option value="desktop">Desktop</option>
          <option value="mobile">Mobile</option>
          <option value="tablet">Tablet</option>
        </select>
      </Field>
    </div>
    <Field label="Timeout (ms)" hint="1000-120000">
      <input type="number" min="1000" max="120000" step="1000" value={req.timeout_ms}
        onChange={(e) => update({ timeout_ms: parseInt(e.target.value) || 30000 })}
        className="w-full h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand" />
    </Field>
  </Section>
)}
      </Card.Body>
    </Card>

    {/* ============================ RIGHT: CODE + RESULT ============================ */}
    <div className="lg:col-span-3 space-y-4">
      {/* Cost + test bar */}
      <Card>
<Card.Body className="p-4">
  <div className="flex flex-wrap items-center gap-3">
    <CostBadge cost={cost} />
    <div className="flex-1" />
    <Button
      onClick={runTest}
      disabled={busy || !req.url.trim()}
      className="px-5"
    >
      {busy ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Running...
        </>
      ) : (
        <>
          <Play className="h-4 w-4" />
          Test request
        </>
      )}
    </Button>
  </div>
  {error && (
    <div className="mt-3 flex items-start gap-2 p-2.5 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
      <span>{error}</span>
    </div>
  )}
</Card.Body>
      </Card>

      {/* Code preview */}
        <Card>
          <Card.Header className="flex-row items-center justify-between gap-2 space-y-0">
            <div className="flex items-center gap-2">
              <Code2 className="h-4 w-4 text-brand" />
              <Card.Title>Code</Card.Title>
            </div>
            <div className="flex items-center gap-2">
              {/* Phase-19: language dropdown — replaces the old
                  button-row. Easier to scan with 12 languages. */}
              <LangDropdown lang={lang} onChange={setLang} />
              <Button variant="secondary" size="sm" onClick={copySnippet}>
                {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <CodeView snippet={snippet} lang={lang} />
          </Card.Body>
        </Card>

      {/* Result */}
      <Card>
<Card.Header className="flex-col items-stretch gap-3 space-y-0 p-4">
  <div className="flex items-center justify-between gap-2 w-full">
    <div className="flex items-center gap-2">
      <FileText className="h-4 w-4 text-brand" />
      <Card.Title>Result</Card.Title>
      {/* F-03: status badge uses the real status_code returned
          by the backend (renamed from `status` in Phase-19).
          Shows green for 2xx, amber for 3xx, red for 4xx/5xx.
          Falls back to 200 if missing for backwards-compat. */}
      {result && !busy && (
        <StatusBadge
          statusCode={result.status_code}
          detail={result.detail}
          success={result.success}
        />
      )}
    </div>
    <div className="flex items-center gap-2">
      {elapsedMs != null && !busy && (
        <span className="text-2xs text-fg-muted">
          Executed in <span className="font-mono font-semibold text-fg">{elapsedMs.toLocaleString()} ms</span>
        </span>
      )}
      {result && result.cost && !busy && (
        <Badge variant="default">
          {result.cost.total} credit{result.cost.total !== 1 ? 's' : ''}
          <span className="text-fg-muted ml-1 font-normal">withdrawn</span>
        </Badge>
      )}
      {/* F-05: show which engine actually served the request
          (tls_client_fast / playwright_browser / datames_stealth) */}
      {result && result.engine && !busy && (
        <span className="inline-flex items-center gap-1 text-2xs text-fg-muted">
          <span className="font-mono">{result.engine}</span>
          {result.challenge && (
            <Badge variant="warning">CF challenge</Badge>
          )}
        </span>
      )}
    </div>
  </div>
  {/* F-04: collapsible headers panel — shows the upstream
      response headers so the user can debug redirects,
      content-type, rate-limit headers, etc. */}
  {result && result.headers && !busy && (
    <details className="text-xs text-fg-muted">
      <summary className="cursor-pointer select-none py-1 hover:text-fg">
        Response headers ({Object.keys(result.headers).length})
      </summary>
      <pre className="px-3 py-2 mt-1 rounded-md bg-surface-2/60 text-2xs font-mono overflow-x-auto max-h-48">
{Object.entries(result.headers)
  .map(([k, v]) => `${k}: ${v}`)
  .join('\n')}
      </pre>
    </details>
  )}
  {/* Tab bar: Result / Browser preview */}
  {result && !busy && (result.body || result.screenshot) && (
    <ResultTabs
      tabs={[
        { key: 'result',     label: 'Result',          visible: !!result.body || !!result.screenshot },
        { key: 'browser',    label: 'Browser preview', visible: !!result.body && !result.screenshot && (req.response_type === 'html' || req.response_type === '') },
        { key: 'screenshot', label: 'Screenshot',      visible: !!result.screenshot },
      ]}
      active={resultTab}
      onChange={setResultTab}
    />
  )}
</Card.Header>
<Card.Body className="p-0">
  <ResultView
    busy={busy}
    error={error}
    result={result}
    responseType={req.response_type}
    screenshot={req.screenshot}
    showRaw={showRaw}
    activeTab={resultTab}
    onToggleRaw={() => setShowRaw((v) => !v)}
  />
</Card.Body>
      </Card>
    </div>
  </div>
</div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function Section({ label, icon: Icon, children }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-fg-subtle" />
        <span className="text-2xs font-semibold text-fg-muted uppercase tracking-wider">{label}</span>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-2xs font-medium text-fg-muted">{label}</label>
        {hint && <span className="text-2xs text-fg-subtle">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Toggle({ label, description, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <div className="min-w-0">
        <div className="text-sm font-medium text-fg">{label}</div>
        {description && <div className="text-2xs text-fg-muted mt-0.5 leading-relaxed">{description}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-full transition-colors mt-0.5',
          checked ? 'bg-brand' : 'bg-surface-3',
        )}
      >
        <span
          aria-hidden="true"
          className="absolute top-0.5 h-4 w-4 rounded-full bg-offwhite shadow transition-[left] duration-200 ease-out"
          style={{ left: checked ? '18px' : '2px' }}
        />
      </button>
    </div>
  );
}

function ProxyOption({ active, onClick, label, sub, cost, costLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-all',
        active
          ? 'border-brand bg-brand-softest shadow-sm'
          : 'border-border bg-surface-1 hover:border-border-strong hover:bg-surface-2',
      )}
    >
      <div className="flex items-center justify-between w-full">
        <span className={cn('text-xs font-semibold', active ? 'text-brand' : 'text-fg')}>{label}</span>
        <span className={cn('text-2xs tabular-nums', active ? 'text-brand font-semibold' : 'text-fg-muted')}>
          {cost} <span className="text-fg-subtle">{costLabel}</span>
        </span>
      </div>
      <span className="text-2xs text-fg-muted">{sub}</span>
    </button>
  );
}

function CostBadge({ cost }) {
  const tone = cost.total >= 4 ? 'warning' : cost.total > 1 ? 'brand' : 'success';
  const label = cost.total === 1 ? 'credit' : 'credits';
  return (
    <div className={cn(
      'inline-flex items-center gap-2 px-3 py-2 rounded-lg border',
      tone === 'warning' && 'bg-warning-soft border-warning/30 text-warning-fg',
      tone === 'brand' && 'bg-brand-softest border-brand/30 text-brand',
      tone === 'success' && 'bg-success-soft border-success/30 text-success-fg',
    )}>
      <Zap className="h-3.5 w-3.5" />
      <div className="text-xs">
        <span className="font-semibold tabular-nums">{cost.total}</span>
        <span className="ml-1">{label}</span>
        <span className="ml-1.5 text-fg-muted opacity-70">
          ({cost.baseLabel}{cost.extrasTotal > 0 ? ` + ${cost.extrasTotal}` : ''})
        </span>
      </div>
    </div>
  );
}

// =============================================================================
// LangDropdown — language picker as a select-style dropdown.
// Shows the current label + caret; clicking opens a list of all
// supported languages. This replaced the previous button-row picker
// because we have 12 languages and a row of 12 pills is too wide
// for the sidebar.
// =============================================================================
function LangDropdown({ lang, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGS.find((l) => l.key === lang) || LANGS[0];

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return;
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-between gap-2 min-w-[170px] px-2.5 py-1.5 text-xs font-medium rounded-md border border-border bg-surface text-fg hover:bg-surface-2 transition-colors"
      >
        <span className="inline-flex items-center gap-1.5">
          <Code className="h-3 w-3 text-fg-muted" />
          {current.label}
        </span>
        <ChevronDown className={cn('h-3 w-3 text-fg-muted transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 min-w-[220px] z-30 bg-surface border border-border-strong rounded-md shadow-lg py-1 max-h-80 overflow-y-auto">
          {LANGS.map((l) => (
            <button
              key={l.key}
              type="button"
              onClick={() => { onChange(l.key); setOpen(false); }}
              className={cn(
                'w-full text-left px-3 py-1.5 text-xs hover:bg-surface-2 transition-colors flex items-center justify-between gap-2',
                lang === l.key ? 'bg-brand/10 text-fg' : 'text-fg-muted hover:text-fg',
              )}
            >
              <span>{l.label}</span>
              {lang === l.key && <Check className="h-3 w-3 text-brand" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// ResultTabs — sub-navigation inside the Result card header.
// Tabs: Result (raw body), Browser preview (HTML iframe), Screenshot.
// Only tabs that are relevant to the current response are shown.
// =============================================================================
function ResultTabs({ tabs, active, onChange }) {
  const visible = tabs.filter((t) => t.visible);
  if (visible.length < 2) return null;  // No need for a tab bar with 1 option
  return (
    <div className="border-b border-border -mx-1">
      <div className="flex items-center gap-0.5">
        {visible.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={cn(
              'relative px-3 py-2 text-xs font-medium transition-colors',
              active === t.key
                ? 'text-fg'
                : 'text-fg-muted hover:text-fg',
            )}
          >
            {t.label}
            {active === t.key && (
              <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-brand rounded-t" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// CodeView — syntax-highlighted code block with a dark "developer"
// background. Uses a lightweight highlighter (no external deps) so we
// don't bloat the bundle. Each language gets basic token coloring:
// keywords, strings, comments, numbers, URLs.
// =============================================================================
function CodeView({ snippet, lang }) {
  const tokens = useMemo(() => highlight(snippet, lang), [snippet, lang]);
  return (
    <pre className="px-4 py-3.5 text-2xs font-mono leading-relaxed overflow-x-auto max-h-[28rem] overflow-y-auto bg-[#0f172a] text-slate-200">
      {tokens.map((line, i) => (
        <div key={i} className="flex">
          <span className="select-none text-slate-500 w-7 shrink-0 pr-3 text-right">
            {i + 1}
          </span>
          <span className="whitespace-pre">
            {line.map((t, j) => (
              <span key={j} className={tokenClass(t.type)}>{t.text}</span>
            ))}
          </span>
        </div>
      ))}
    </pre>
  );
}

function tokenClass(type) {
  switch (type) {
    case 'keyword': return 'text-pink-400';
    case 'string':  return 'text-emerald-300';
    case 'number':  return 'text-amber-300';
    case 'comment': return 'text-slate-500 italic';
    case 'function': return 'text-sky-300';
    case 'url':     return 'text-cyan-300 underline';
    case 'header':  return 'text-amber-200 font-semibold';
    default:       return '';
  }
}

// Tiny language-aware tokenizer. Not a real parser — just enough color
// to make code readable. Order of patterns matters: comments first
// (so they don't get tokenized as strings), then strings, keywords,
// numbers, URLs.
const LANG_PATTERNS = {
  curl: {
    keywords: ['curl', '-X', '-H', '-d', '-F', '-u', '-L', '-G', '-k', '-i', '-s', '-o'],
    strings: /(['"])(?:(?=(\\?))\2.)*?\1/g,
    comments: /^#.*$/gm,
  },
  python: {
    keywords: ['import', 'from', 'as', 'def', 'class', 'return', 'if', 'else', 'elif', 'for', 'in', 'while', 'try', 'except', 'finally', 'with', 'async', 'await', 'lambda', 'pass', 'break', 'continue', 'return', 'yield', 'True', 'False', 'None'],
    strings: /(['"])(?:(?=(\\?))\2.)*?\1|("""[\s\S]*?"""|'''[\s\S]*?''')/g,
    comments: /#.*$/gm,
    decorators: /^@\w+/gm,
  },
  python_aio: { alias: 'python' },
  ruby: {
    keywords: ['require', 'class', 'def', 'end', 'do', 'begin', 'rescue', 'raise', 'puts', 'nil', 'true', 'false', 'return', 'if', 'else', 'elsif', 'unless', 'while', 'until', 'do', 'each', 'map', 'attr_accessor', 'attr_reader', 'attr_writer'],
    strings: /(['"])(?:(?=(\\?))\2.)*?\1/g,
    comments: /^#.*$/gm,
  },
  go: {
    keywords: ['package', 'import', 'func', 'var', 'const', 'type', 'struct', 'interface', 'return', 'if', 'else', 'for', 'range', 'switch', 'case', 'default', 'break', 'continue', 'go', 'defer', 'chan', 'select', 'map', 'true', 'false', 'nil'],
    strings: /(['"`])(?:(?=(\\?))\2.)*?\1/g,
    comments: /\/\/.*$|\/\*[\s\S]*?\*\//gm,
  },
  node: {
    keywords: ['const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'default', 'break', 'continue', 'class', 'extends', 'new', 'this', 'async', 'await', 'try', 'catch', 'finally', 'throw', 'export', 'import', 'from', 'of', 'in', 'typeof', 'instanceof', 'true', 'false', 'null', 'undefined'],
    strings: /(['"`])(?:(?=(\\?))\2.)*?\1/g,
    comments: /\/\/.*$|\/\*[\s\S]*?\*\//gm,
  },
  node_axios: { alias: 'node' },
  php: {
    keywords: ['function', 'return', 'if', 'else', 'elseif', 'for', 'foreach', 'while', 'do', 'switch', 'case', 'default', 'break', 'continue', 'class', 'extends', 'implements', 'public', 'private', 'protected', 'static', 'new', 'use', 'namespace', 'as', 'true', 'false', 'null'],
    strings: /(['"])(?:(?=(\\?))\2.)*?\1/g,
    comments: /\/\/.*$|\/\*[\s\S]*?\*\//gm,
    variables: /\$\w+/g,
  },
  java: {
    keywords: ['public', 'private', 'protected', 'static', 'final', 'class', 'interface', 'extends', 'implements', 'new', 'return', 'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'default', 'break', 'continue', 'try', 'catch', 'finally', 'throw', 'throws', 'import', 'package', 'void', 'int', 'String', 'boolean', 'double', 'float', 'long', 'true', 'false', 'null'],
    strings: /(['"])(?:(?=(\\?))\2.)*?\1/g,
    comments: /\/\/.*$|\/\*[\s\S]*?\*\//gm,
  },
  csharp: {
    keywords: ['public', 'private', 'protected', 'static', 'class', 'interface', 'new', 'return', 'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'default', 'break', 'continue', 'try', 'catch', 'finally', 'throw', 'using', 'namespace', 'async', 'await', 'var', 'void', 'string', 'int', 'bool', 'true', 'false', 'null'],
    strings: /(['"])(?:(?=(\\?))\2.)*?\1|(@")[\s\S]*?(")/g,
    comments: /\/\/.*$|\/\*[\s\S]*?\*\//gm,
  },
  rust: {
    keywords: ['fn', 'let', 'mut', 'const', 'static', 'pub', 'use', 'mod', 'struct', 'enum', 'trait', 'impl', 'for', 'while', 'loop', 'if', 'else', 'match', 'return', 'break', 'continue', 'async', 'await', 'self', 'Self', 'true', 'false', 'Ok', 'Err', 'Some', 'None'],
    strings: /(['"])(?:(?=(\\?))\2.)*?\1/g,
    comments: /\/\/.*$|\/\*[\s\S]*?\*\//gm,
    macros: /\w+!/g,
  },
  shell: {
    keywords: ['echo', 'cd', 'ls', 'cat', 'grep', 'sed', 'awk', 'cut', 'sort', 'uniq', 'head', 'tail', 'curl', 'wget', 'export', 'set', 'unset', 'source', 'if', 'then', 'else', 'elif', 'fi', 'for', 'while', 'do', 'done', 'case', 'esac', 'function', 'return', 'exit'],
    strings: /(['"])(?:(?=(\\?))\2.)*?\1/g,
    comments: /#.*$/gm,
    variables: /\$\{?\w+\}?/g,
  },
};

// Tokenize a single line for a given language.
function tokenizeLine(line, lang) {
  let effectiveLang = lang;
  if (LANG_PATTERNS[lang]?.alias) effectiveLang = LANG_PATTERNS[lang].alias;
  const cfg = LANG_PATTERNS[effectiveLang] || {};

  // 1. Whole-line comments
  if (cfg.comments) {
    const m = cfg.comments.exec(line);
    if (m && m.index === 0) {
      return [{ type: 'comment', text: line }];
    }
  }

  // 2. Split into tokens. We walk the line, greedily matching strings,
  //    then keywords/identifiers, then numbers, then URLs.
  const out = [];
  let i = 0;
  const kw = new Set(cfg.keywords || []);
  while (i < line.length) {
    const ch = line[i];
    // string match
    const stringRe = cfg.strings;
    if (stringRe) {
      stringRe.lastIndex = 0;
      const slice = line.slice(i);
      const sm = stringRe.exec(slice);
      if (sm && sm.index === 0) {
        out.push({ type: 'string', text: sm[0] });
        i += sm[0].length;
        continue;
      }
    }
    // variable / macro
    if (cfg.variables && cfg.variables.test(line.slice(i, i + 32))) {
      const m = line.slice(i).match(cfg.variables);
      if (m && m.index === 0) {
        out.push({ type: 'function', text: m[0] });
        i += m[0].length;
        continue;
      }
    }
    if (cfg.macros) {
      const slice = line.slice(i);
      const mm = slice.match(cfg.macros);
      if (mm && mm.index === 0) {
        out.push({ type: 'function', text: mm[0] });
        i += mm[0].length;
        continue;
      }
    }
    if (cfg.decorators) {
      const slice = line.slice(i);
      const dm = slice.match(cfg.decorators);
      if (dm && dm.index === 0) {
        out.push({ type: 'function', text: dm[0] });
        i += dm[0].length;
        continue;
      }
    }
    // URLs (https://...)
    if (/^https?:\/\//.test(line.slice(i))) {
      const m = line.slice(i).match(/^https?:\/\/\S+/);
      if (m) {
        out.push({ type: 'url', text: m[0] });
        i += m[0].length;
        continue;
      }
    }
    // numbers
    if (/[0-9]/.test(ch)) {
      const m = line.slice(i).match(/^[0-9]+(\.[0-9]+)?/);
      if (m) {
        out.push({ type: 'number', text: m[0] });
        i += m[0].length;
        continue;
      }
    }
    // identifiers / keywords
    if (/[A-Za-z_]/.test(ch)) {
      const m = line.slice(i).match(/^[A-Za-z_][A-Za-z0-9_]*/);
      if (m) {
        const word = m[0];
        // keyword?
        if (kw.has(word)) {
          out.push({ type: 'keyword', text: word });
        }
        // function call?
        else if (line[i + word.length] === '(') {
          out.push({ type: 'function', text: word });
        }
        else {
          out.push({ type: 'plain', text: word });
        }
        i += word.length;
        continue;
      }
    }
    // default: emit one char
    out.push({ type: 'plain', text: ch });
    i += 1;
  }
  return out;
}

function highlight(snippet, lang) {
  if (!snippet) return [];
  return snippet.split('\n').map((line) => tokenizeLine(line, lang));
}

// F-06: OutputsPicker — multi-select chip group for the `outputs` field.
// null = "all fields" (no filter). Selecting specific keys narrows the
// response payload. Selecting all = null too (so the field is omitted).
const OUTPUT_CHOICES = [
  { key: 'success',      label: 'success' },
  { key: 'engine',       label: 'engine' },
  { key: 'url',          label: 'url' },
  { key: 'status_code',  label: 'status_code' },
  { key: 'headers',      label: 'headers' },
  { key: 'body',         label: 'body' },
  { key: 'screenshot',   label: 'screenshot' },
  { key: 'cost',         label: 'cost' },
  { key: 'challenge',    label: 'challenge' },
];
function OutputsPicker({ value, onChange }) {
  // value is null (all) or string[] of selected keys.
  const selected = new Set(value || []);
  function toggle(k) {
    const next = new Set(selected);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    if (next.size === 0 || next.size === OUTPUT_CHOICES.length) onChange(null);
    else onChange([...next]);
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {OUTPUT_CHOICES.map((c) => {
        const on = selected.has(c.key);
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => toggle(c.key)}
            className={cn(
              'px-2 py-1 text-2xs font-mono rounded-md border transition-colors',
              on
                ? 'bg-brand/15 border-brand/40 text-fg'
                : 'bg-surface-2 border-border text-fg-muted hover:text-fg'
            )}
          >
            {c.label}
          </button>
        );
      })}
      <span className="text-2xs text-fg-subtle self-center">
        {value === null ? '(all fields)' : `(${value.length}/${OUTPUT_CHOICES.length})`}
      </span>
    </div>
  );
}

function StatusBadge({ statusCode, detail, success }) {
  // F-03: pick the badge label + variant from the actual status code.
  // status_code can be null (some engines don't track it); default to
  // 200 for backwards-compat. detail = error from the API.
  const sc = statusCode ?? (detail ? 500 : 200);
  const label = detail ? 'Error' : success === false ? 'Failed' : `${sc} OK`;
  let variant = 'success';
  if (detail || sc >= 400) variant = 'destructive';
  else if (sc >= 300 && sc < 400) variant = 'warning';
  return (
    <Badge variant={variant}>{label}</Badge>
  );
}

function ResultView({ busy, error, result, responseType, screenshot, showRaw, activeTab = 'result', onToggleRaw }) {
  if (busy) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-fg-muted">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-xs">Running your request…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-fg-muted text-sm">
        <AlertTriangle className="h-5 w-5 mx-auto mb-2 text-destructive" />
        {error}
      </div>
    );
  }

  if (!result) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-fg-muted">
        <Square className="h-5 w-5" />
        <span className="text-xs">No result yet — hit "Test request" to see the response.</span>
      </div>
    );
  }

  // Error from API
  if (result.detail) {
    return (
      <pre className="px-4 py-3 text-2xs font-mono leading-relaxed text-destructive overflow-x-auto max-h-72">
{JSON.stringify(result, null, 2)}
      </pre>
    );
  }

  // Screenshot tab (explicit selection)
  if (activeTab === 'screenshot' && result.screenshot) {
    return (
      <div className="p-3">
        <div className="rounded-md overflow-hidden border border-border bg-surface-2">
          <img
            src={result.screenshot}
            alt="Screenshot result"
            className="w-full h-auto"
          />
        </div>
        <a
          href={result.screenshot}
          download="screenshot.png"
          className="inline-flex items-center gap-1.5 mt-2 text-2xs text-brand hover:underline"
        >
          <Download className="h-3 w-3" />
          Download PNG
        </a>
      </div>
    );
  }

  // Browser preview tab (HTML iframe) — for HTML responses only
  if (activeTab === 'browser') {
    const html = pickBodyText(result, responseType);
    if (html) {
      return (
        <iframe
          title="Browser preview"
          srcDoc={html}
          className="w-full h-[28rem] border-0 bg-offwhite"
          sandbox="allow-same-origin"
        />
      );
    }
  }

  // Raw JSON mode (toggle inside the result tab)
  if (showRaw) {
    return (
      <div className="relative">
        <button
          type="button"
          onClick={onToggleRaw}
          className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 text-2xs font-medium rounded-md bg-surface-2 text-fg-muted hover:text-fg border border-border"
        >
          <EyeOff className="h-3 w-3" /> Show rendered
        </button>
        <pre className="px-4 py-3 text-2xs font-mono leading-relaxed text-fg overflow-x-auto max-h-[28rem]">
{JSON.stringify(result, null, 2)}
        </pre>
      </div>
    );
  }

  // Screenshot fallback (when screenshot tab is selected but body exists too)
  if (screenshot && result.screenshot && activeTab === 'result') {
    return (
      <div className="p-3">
        <div className="rounded-md overflow-hidden border border-border bg-surface-2">
          <img
            src={result.screenshot}
            alt="Screenshot result"
            className="w-full h-auto"
          />
        </div>
        <a
          href={result.screenshot}
          download="screenshot.png"
          className="inline-flex items-center gap-1.5 mt-2 text-2xs text-brand hover:underline"
        >
          <Download className="h-3 w-3" />
          Download
        </a>
      </div>
    );
  }

  // PDF
  if (responseType === 'pdf' && (result.body || result.pdf || result.data)) {
    const pdfData = result.body || result.pdf || result.data;
    return (
      <iframe
        title="PDF result"
        src={pdfData.startsWith('data:') ? pdfData : `data:application/pdf;base64,${pdfData}`}
        className="w-full h-[28rem] border-0"
      />
    );
  }

  // Default: raw body in a syntax-highlighted dark code block + "view
  // in browser" toggle button.
  const bodyText = pickBodyText(result, responseType);
  if (bodyText) {
    return (
      <div className="relative">
        {responseType === 'html' && (
          <button
            type="button"
            onClick={onToggleRaw}
            className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-1 text-2xs font-medium rounded-md bg-surface-2 text-fg-muted hover:text-fg border border-border"
            title="Open the browser preview tab instead"
          >
            <Eye className="h-3 w-3" /> Open in browser
          </button>
        )}
        <CodeView snippet={bodyText} lang="" />
      </div>
    );
  }

  // Fallback: pretty-printed JSON
  return (
    <div className="relative">
      {onToggleRaw && (
        <button
          type="button"
          onClick={onToggleRaw}
          className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 text-2xs font-medium rounded-md bg-surface-2 text-fg-muted hover:text-fg border border-border"
        >
          {showRaw ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {showRaw ? 'Show rendered' : 'Show raw JSON'}
        </button>
      )}
      <pre className="px-4 py-3 text-2xs font-mono leading-relaxed text-fg overflow-x-auto max-h-[28rem]">
{JSON.stringify(result, null, 2)}
      </pre>
    </div>
  );
}

function pickBodyText(result, responseType) {
  // Backend returns `body` as the raw text. For JSON mode the user gets
  // result.data (already parsed) — we still show a representation.
  if (typeof result.body === 'string' && result.body.length > 0) return result.body;
  if (typeof result.data === 'string' && result.data.length > 0) return result.data;
  if (result.data && typeof result.data === 'object') return JSON.stringify(result.data, null, 2);
  if (result.body && typeof result.body === 'object') return JSON.stringify(result.body, null, 2);
  return null;
}

// =============================================================================
// Payload builder + code snippets
// =============================================================================

/**
 * Strip empty / default values so the curl example stays readable.
 * Also coerce "0/0/0/0" defaults off so the snippet doesn't get noisy.
 */
function cleanPayload(req) {
  const out = { url: req.url.trim() };
  if (req.method && req.method !== 'GET') out.method = req.method;
  if (req.payload) out.payload = req.payload;
  if (req.js_render) out.js_render = true;
  if (req.js_render && req.js_instructions?.trim()) out.js_instructions = req.js_instructions.trim();
  if (req.custom_headers) out.custom_headers = req.custom_headers;
  if (req.proxy_mode) out.proxy_mode = req.proxy_mode;
  if (req.proxy_country?.trim()) out.proxy_country = req.proxy_country.trim();
  // Per-request proxy override. Skip when the field is empty (use the
  // operator slot). ``"direct"`` is sent through as-is — the backend
  // interprets it as "skip the operator slot, go direct". A real URL
  // is also sent through; the backend validates scheme and the engines
  // build the right FlareSolverr / tls_client / Playwright shape.
  if (req.proxy_url?.trim()) out.proxy_url = req.proxy_url.trim();
  if (req.proxy_username?.trim()) out.proxy_username = req.proxy_username.trim();
  if (req.proxy_password?.trim()) out.proxy_password = req.proxy_password;
  if (req.session_id?.trim()) out.session_id = req.session_id.trim();
  if (req.tag?.trim()) out.tag = req.tag.trim();
  if (req.timeout_ms && req.timeout_ms !== 30000) out.timeout_ms = req.timeout_ms;
  if (req.response_type && req.response_type !== 'html') out.response_type = req.response_type;
  if (req.outputs?.length) out.outputs = req.outputs;
  if (req.allowed_status_codes && JSON.stringify(req.allowed_status_codes) !== '[200,201,202,203,204,206]') {
    out.allowed_status_codes = req.allowed_status_codes;
  }
  if (req.wait_for?.trim()) out.wait_for = req.wait_for.trim();
  if (req.wait && req.wait !== 0) out.wait = req.wait;
  if (req.block_resources === false) out.block_resources = false;
  if (req.json_response) out.json_response = true;
  if (req.css_extractor) out.css_extractor = req.css_extractor;
  if (req.autoparse) out.autoparse = true;
  if (req.permissive_status) out.permissive_status = true;
  // F-02: trigger the screenshot block on the `screenshot` boolean only.
  // `req.response_type === 'screenshot'` would never happen now because
  // we removed that option from the dropdown.
  if (req.screenshot) {
    out.screenshot = true;
    if (req.screenshot_fullpage) out.screenshot_fullpage = true;
    if (req.screenshot_selector?.trim()) out.screenshot_selector = req.screenshot_selector.trim();
    if (req.screenshot_format && req.screenshot_format !== 'png') out.screenshot_format = req.screenshot_format;
    if (req.screenshot_format === 'jpeg' && req.screenshot_quality !== 90) {
      out.screenshot_quality = req.screenshot_quality;
    }
  }
  if (req.viewport_width !== 1920 || req.viewport_height !== 1080) {
    out.viewport_width = req.viewport_width;
    out.viewport_height = req.viewport_height;
  }
  if (req.device && req.device !== 'desktop') out.device = req.device;
  if (req.cookies?.length) out.cookies = req.cookies;
  return out;
}

function buildSnippet(req, lang, apiKey) {
  const body = cleanPayload(req);
  const json = JSON.stringify(body, null, 2);
  const jsonInline = JSON.stringify(body);
  const keyLine = apiKey ? ` -H "x-api-key: *** \\` : '';
  // Mirror api.js: VITE_API_BASE in prod, /api in dev (Vite proxy).
  const endpoint = `${import.meta.env.VITE_API_BASE || (import.meta.env.DEV ? '/api' : '')}/v1/fetch`;

  // Headers block used by every language (HTTP API key)
  const hdr = (indent = '  ') => apiKey
    ? `${indent}-H "x-api-key: ${apiKey}"`
    : '';

  switch (lang) {
    case 'curl':
      return [
        `curl -X POST "${endpoint}" \\`,
        `  -H "Content-Type: application/json"${hdr('  ')} \\`,
        `  -d '${json.replace(/'/g, "\\'")}'`,
      ].join('\n');

    case 'python':
      return [
        'import requests',
        '',
        'response = requests.post(',
        `    "${endpoint}",`,
        apiKey ? `    headers={"x-api-key": "${apiKey}"},` : null,
        '    json=' + json + ',',
        ')',
        '',
        'print(response.json())',
      ].filter(Boolean).join('\n');

    case 'python_aio':
      return [
        'import aiohttp',
        'import asyncio',
        '',
        'async def fetch():',
        '    async with aiohttp.ClientSession() as session:',
        `        async with session.post(`,
        `            "${endpoint}",`,
        apiKey ? `            headers={"x-api-key": "${apiKey}"},` : null,
        '            json=' + json + ',',
        '        ) as resp:',
        '            return await resp.json()',
        '',
        'print(asyncio.run(fetch()))',
      ].filter(Boolean).join('\n');

    case 'node':
      return [
        `const response = await fetch("${endpoint}", {`,
        `  method: "POST",`,
        apiKey
          ? `  headers: { "Content-Type": "application/json", "x-api-key": "${apiKey}" },`
          : `  headers: { "Content-Type": "application/json" },`,
        `  body: JSON.stringify(${json}),`,
        `});`,
        `const data = await response.json();`,
        `console.log(data);`,
      ].join('\n');

    case 'node_axios':
      return [
        `const axios = require('axios');`,
        '',
        `axios.post("${endpoint}", ${json}, {`,
        apiKey
          ? `  headers: { "x-api-key": "${apiKey}" },`
          : null,
        '})',
        `  .then(res => console.log(res.data))`,
        `  .catch(err => console.error(err));`,
      ].filter(Boolean).join('\n');

    case 'ruby':
      return [
        'require "net/http"',
        'require "uri"',
        'require "json"',
        '',
        'uri = URI("' + endpoint + '")',
        'http = Net::HTTP.new(uri.host, uri.port)',
        'http.use_ssl = (uri.scheme == "https")',
        '',
        'request = Net::HTTP::Post.new(uri)',
        'request["Content-Type"] = "application/json"',
        apiKey ? `request["x-api-key"] = "${apiKey}"` : null,
        `request.body = ${jsonInline}.to_json`,
        '',
        'response = http.request(request)',
        'puts response.body',
      ].filter(Boolean).join('\n');

    case 'go':
      return [
        'package main',
        '',
        'import (',
        '  "bytes"',
        '  "encoding/json"',
        '  "fmt"',
        '  "net/http"',
        ')',
        '',
        'func main() {',
        '  body, _ := json.Marshal(' + json + ')',
        '  req, _ := http.NewRequest("POST", "' + endpoint + '", bytes.NewReader(body))',
        '  req.Header.Set("Content-Type", "application/json")',
        apiKey ? `  req.Header.Set("x-api-key", "${apiKey}")` : null,
        '  resp, err := http.DefaultClient.Do(req)',
        '  if err != nil { panic(err) }',
        '  defer resp.Body.Close()',
        '  fmt.Println(resp.Status)',
        '}',
      ].filter(Boolean).join('\n');

    case 'php':
      return [
        '<?php',
        '$ch = curl_init("' + endpoint + '");',
        'curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);',
        'curl_setopt($ch, CURLOPT_POST, true);',
        'curl_setopt($ch, CURLOPT_HTTPHEADER, [',
        '    "Content-Type: application/json"' + (apiKey ? `,\n    "x-api-key: ${apiKey}"` : '') + ',',
        ']);',
        'curl_setopt($ch, CURLOPT_POSTFIELDS, \'' + jsonInline.replace(/'/g, "\\'") + '\');',
        '$response = curl_exec($ch);',
        'echo $response;',
        'curl_close($ch);',
      ].join('\n');

    case 'java':
      return [
        'import java.net.HttpURLConnection;',
        'import java.net.URL;',
        'import java.nio.charset.StandardCharsets;',
        '',
        'public class DatamesClient {',
        '  public static void main(String[] args) throws Exception {',
        `    URL url = new URL("${endpoint}");`,
        '    HttpURLConnection conn = (HttpURLConnection) url.openConnection();',
        '    conn.setRequestMethod("POST");',
        '    conn.setRequestProperty("Content-Type", "application/json");',
        apiKey ? `    conn.setRequestProperty("x-api-key", "${apiKey}");` : null,
        `    conn.setDoOutput(true);`,
        `    byte[] body = ${jsonInline}.getBytes(StandardCharsets.UTF_8);`,
        `    conn.getOutputStream().write(body);`,
        `    System.out.println(conn.getResponseCode());`,
        '  }',
        '}',
      ].filter(Boolean).join('\n');

    case 'csharp':
      return [
        'using System;',
        'using System.Net.Http;',
        'using System.Text;',
        'using System.Threading.Tasks;',
        '',
        'class Program {',
        '  static async Task Main() {',
        `    var client = new HttpClient();`,
        apiKey ? `    client.DefaultRequestHeaders.Add("x-api-key", "${apiKey}");` : null,
        `    var content = new StringContent(${jsonInline}, Encoding.UTF8, "application/json");`,
        `    var response = await client.PostAsync("${endpoint}", content);`,
        '    Console.WriteLine(await response.Content.ReadAsStringAsync());',
        '  }',
        '}',
      ].filter(Boolean).join('\n');

    case 'rust':
      return [
        'use reqwest::header::{HeaderMap, HeaderValue, CONTENT_TYPE};',
        'use serde_json::json;',
        '',
        '#[tokio::main]',
        'async fn main() -> Result<(), Box<dyn std::error::Error>> {',
        '  let mut headers = HeaderMap::new();',
        '  headers.insert(CONTENT_TYPE, HeaderValue::from_static("application/json"));',
        apiKey ? `  headers.insert("x-api-key", HeaderValue::from_str("${apiKey}")?);` : null,
        '',
        `  let body = json!(${json});`,
        `  let res = reqwest::Client::new()`,
        `    .post("${endpoint}")`,
        `    .headers(headers)`,
        `    .json(&body)`,
        `    .send()`,
        `    .await?;`,
        '',
        '  println!("{}", res.status());',
        '  Ok(())',
        '}',
      ].join('\n');

    case 'shell':
      return [
        '#!/usr/bin/env bash',
        apiKey ? `API_KEY="${apiKey}"` : '# API_KEY="your-key-here"',
        '',
        `curl -X POST "${endpoint}" \\`,
        '  -H "Content-Type: application/json" \\',
        apiKey ? '  -H "x-api-key: ${API_KEY}" \\' : null,
        `  -d \'${jsonInline.replace(/'/g, "'\\''")}\'`,
        '',
        '# Or set the key as an env var for safety:',
        '# export SCRAPERDATAMES_API_KEY="your-key-here"',
      ].filter(Boolean).join('\n');

    default:
      return '';
  }
}